import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Infinity as InfinityIcon } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
  }),
};

const HeroLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      custom={i}
      variants={line}
      initial="hidden"
      animate="visible"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const orbScale = useTransform(scrollYProgress, [0, 1], [1, 1.35]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-white via-white/40 to-white pointer-events-none" />

      {/* Parallax gradient orb */}
      <motion.div
        style={{ y: orbY, scale: orbScale }}
        className="absolute -right-24 top-24 md:top-16 w-[380px] h-[380px] md:w-[560px] md:h-[560px] rounded-full brand-gradient blur-[90px] opacity-30"
      />
      <motion.div
        style={{ y: orbY }}
        className="absolute right-[8%] top-[22%] hidden lg:flex items-center justify-center"
      >
        <motion.div
          animate={{ rotate: [0, 6, -6, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="w-40 h-40 xl:w-56 xl:h-56 rounded-[2rem] brand-gradient flex items-center justify-center shadow-[0_30px_80px_-20px_rgba(0,168,232,0.5)]"
        >
          <InfinityIcon className="w-20 h-20 xl:w-28 xl:h-28 text-white" strokeWidth={2.4} />
        </motion.div>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full pt-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 brand-gradient" />
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">
            IT-Beratung · Cloud · Security
          </span>
        </motion.div>

        <h1 className="font-display font-semibold tracking-tight leading-[0.92] text-[16vw] sm:text-[13vw] md:text-8xl lg:text-[8.5rem] text-zinc-950">
          <HeroLine i={0}>Ihre IT.</HeroLine>
          <HeroLine i={1}>
            <span className="brand-text">Grenzenlos</span> gedacht.
          </HeroLine>
          <HeroLine i={2}>
            <span className="brand-text">Sicher</span> gemacht.
          </HeroLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg text-zinc-600 leading-relaxed"
        >
          inn&apos;spective plant, betreut und schützt Ihre IT — von der
          On-Premises-Infrastruktur über Microsoft&nbsp;365 bis zur IT-Security.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#kontakt"
            data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 rounded-full bg-zinc-950 text-white text-sm font-medium px-7 py-4 hover:bg-zinc-800 transition-colors duration-200"
          >
            Projekt anfragen
          </a>
          <a
            href="#leistungen"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-300 text-zinc-900 text-sm font-medium px-7 py-4 hover:border-zinc-900 transition-colors duration-200"
          >
            Leistungen ansehen
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#leistungen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-400"
      >
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Scrollen</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.a>
    </section>
  );
};
